import {
  ChangeDetectorRef,
  Component,
  Inject,
  Injectable,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { User } from 'app/classes/user';
import { UserSearchService } from 'app/core/services/user-search.service';
import { concat, debounce } from 'lodash';
import { Observable, Subscription } from 'rxjs';

/**
 * Input data for user search dialog.
 * @property submitButtonText
 * Label for submit button - "Invite", "Add To Group", "Share Plan", etc.
 * @property searchByEmail
 * Set to false to hide search-by-email tab
 * @property disableSelf
 * Set as true to diable selection of current user. Requires `currentUserUUID`.
 * @property currentUserUUID
 * Value of current user UUID used when disableSelf is set
 */
export interface UserSearchComponentData {
  selectedUsers: User[];
  disableSelectedUsers?: boolean;
  submitButtonText: string;
  searchByEmail: boolean;
  disableSelf?: boolean;
  currentUserUUID?: string;
  excludeBasicUsers?: boolean;
}

/**
 * Output of user search dialog.
 * @property users
 * Array of Users
 * @property emails
 * Array of email addresses
 */
export interface UserSearchComponentResult {
  users: User[];
  emails: string[];
}

@Component({
  selector: 'user-search',
  templateUrl: './user-search.component.html',
  styleUrls: ['./user-search.component.scss'],
})
export class UserSearchComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  private debouncedSearchUsers = debounce(this.searchUsers.bind(this), 500);
  allUsers: User[] = [];
  selectedUsers: User[] = [];
  selectedEmails: string[] = [];
  search: FormControl;
  email: FormControl;
  listOfEmails: FormControl;
  loading: boolean;
  private subs: Subscription[] = [];
  pageNumber = 1;
  limitPerPage = 10;
  totalPages = 0;
  totalUsers = 0;

  dataSource: MatTableDataSource<User> = new MatTableDataSource<User>(
    this.allUsers,
  );
  obs: Observable<any>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UserSearchComponentData,
    private formBuilder: FormBuilder,
    private service: UserSearchService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.loading = true;

    this.subs.push(
      this.service.loadingUsers.subscribe(loading => {
        this.loading = loading;
      }),
    );

    this.search = this.formBuilder.control('');
    this.email = this.formBuilder.control('');
    this.listOfEmails = this.formBuilder.control([]);
    this.searchUsers();

    this.changeDetectorRef.detectChanges();
    this.dataSource.paginator = this.paginator;
    this.obs = this.dataSource.connect();

    this.selectedUsers = this.data.selectedUsers.map(u => new User(u));
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
    if (this.dataSource) {
      this.dataSource.disconnect();
    }
  }

  searchUsers() {
    this.service
      .searchUsers(
        this.search.value || '',
        this.pageNumber,
        this.limitPerPage,
        this.data.excludeBasicUsers ?? false,
      )
      .subscribe(result => {
        this.allUsers = result.users;
        this.totalPages = result.totalPages;
        this.totalUsers = result.total || result.totalPages * this.limitPerPage;
        this.paginator.pageIndex = result.currentPage - 1;
      });
  }

  debounceSearchUsers() {
    this.debouncedSearchUsers();
    this.pageNumber = 1;
    this.changeDetectorRef.detectChanges();
  }

  addRemoveUser(user: User) {
    if (this.data.disableSelf && user.uuid === this.data.currentUserUUID) {
      return;
    }

    const i = this.selectedUsers.findIndex(u => u.uuid === user.uuid);
    if (i > -1) {
      this.selectedUsers = this.selectedUsers.filter(u => u.uuid !== user.uuid);
    } else {
      this.selectedUsers =
        this.selectedUsers.findIndex(u => u.uuid === user.uuid) > -1
          ? this.selectedUsers
          : concat(this.selectedUsers, [user]);
    }
  }

  addEmail() {
    const emails = this.email.value.split(',');
    emails.forEach(email => {
      const i = this.selectedEmails.indexOf(email);
      if (i < 0) {
        this.selectedEmails.push(email);
      }
    });
  }

  removeEmail(email: string) {
    const i = this.selectedEmails.indexOf(email);
    if (i > -1) {
      this.selectedEmails.splice(i, 1);
    }
  }

  onPageEvent($event) {
    const pageIndex = $event.pageIndex;
    this.pageNumber = 1 + pageIndex;
    this.searchUsers();
  }

  userSelected(user: User): boolean {
    return (
      this.data.selectedUsers.findIndex(u => user.uuid === u.uuid) > -1 ||
      this.selectedUsers.findIndex(u => u.uuid === user.uuid) > -1
    );
  }

  isUserDisabled(user: User): boolean {
    return (
      (this.data.disableSelf && user.uuid === this.data.currentUserUUID) ||
      (this.data.disableSelectedUsers &&
        this.data.selectedUsers.some(u => u.uuid === user.uuid))
    );
  }

  selectedUsersExceptDisabled() {
    if (this.data.disableSelectedUsers) {
      return this.selectedUsers.filter(
        u => this.data.selectedUsers.findIndex(u2 => u2.uuid === u.uuid) === -1,
      );
    }

    return this.selectedUsers;
  }
}

@Injectable()
export class CustomPaginator extends MatPaginatorIntl {
  getRangeLabel = (page, pageSize, length): string => {
    const maxPages = Math.ceil(length / pageSize);
    const currentPage = maxPages > 0 ? page + 1 : 0;
    return `Page ${currentPage} of ${maxPages}`;
  }
}
